/* eslint-disable no-use-before-define */
import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import StructureModel from '@Components/Results/structure/structure-model.service';
import StructureZoomService from '@Services/StructureZoom';
import Structure from '@Components/Results/structure/Structure';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import classnames from 'classnames';
import SubstanceIdentificationChemicalNamesList from '@Components/Results/substances/SubstanceIdentificationChemicalNamesList';

import './SubstanceIdentificationData.scss';

const IDENTIFICATION_DATA_CLASS = 'rx-substance-identification';
const IDENTIFICATION_DATA_ID = 'rx-substance-identification';
const ROW_VARIANTS = {
  PAIRED_START: 'paired-start',
  PAIRED_END: 'paired-end',
  SINGLE_COLUMN: 'single-column',
};

export default function SubstanceIdentificationData({ metadata, database, details, zoom }) {
  const zoomClasses = StructureZoomService.getZoomClasses('substances') || [];
  const zoomClass = zoomClasses[zoom];
  return (
    <>
      {renderIdentificationTable(details, database.isCIDatabase, database.isCommercialSupplierData)}
      <div className="align-structures">{renderMixtureItems(details.mixtureItems, database.id, zoomClass, zoom, metadata)}</div>
    </>
  );
}

function getDescriptionListClassName(type, rowVariant) {
  return classnames(`${IDENTIFICATION_DATA_CLASS}__${type}`, {
    [`${IDENTIFICATION_DATA_CLASS}__${type}--${rowVariant}`]: Boolean(rowVariant),
  });
}

function renderDescriptionListEntry(label, value, rowVariant) {
  if (!value) {
    return null;
  }

  return (
    <>
      <dt className={getDescriptionListClassName('term', rowVariant)}>{label}</dt>
      <InnerHTML tag="dd" className={getDescriptionListClassName('description', rowVariant)} html={value} />
    </>
  );
}

function renderEntryPair(firstEntry, secondEntry) {
  const hasFirstEntry = Boolean(firstEntry?.value);
  const hasSecondEntry = Boolean(secondEntry?.value);

  if (!hasFirstEntry && !hasSecondEntry) {
    return null;
  }

  if (hasFirstEntry && hasSecondEntry) {
    return (
      <>
        {renderDescriptionListEntry(firstEntry.label, firstEntry.value, ROW_VARIANTS.PAIRED_START)}
        {renderDescriptionListEntry(secondEntry.label, secondEntry.value, ROW_VARIANTS.PAIRED_END)}
      </>
    );
  }

  const entry = hasFirstEntry ? firstEntry : secondEntry;

  return renderDescriptionListEntry(entry.label, entry.value, ROW_VARIANTS.SINGLE_COLUMN);
}

function getSubstanceType(type, isCIDatabase) {
  if (isCIDatabase || isEmpty(type)) {
    return null;
  }

  return isArray(type) ? type.join(', ') : type;
}

function renderIdentificationIdRow(id, isCIDatabase, isCommercialSupplierData) {
  if (isCIDatabase) {
    return null;
  }

  return renderDescriptionListEntry(isCommercialSupplierData ? 'Commercial Substances ID:' : 'Reaxys ID:', id, ROW_VARIANTS.SINGLE_COLUMN);
}

function renderRegistryRow(casRegistryNumbers, substanceType) {
  if (!casRegistryNumbers) {
    return null;
  }

  return renderEntryPair(
    { label: 'CAS Registry Number(s):', value: casRegistryNumbers },
    { label: 'Substance type:', value: substanceType }
  );
}

function renderFormulaRow(formula, linearFormula, isCommercialSupplierData) {
  if (!formula && !linearFormula) {
    return null;
  }

  return renderEntryPair(
    { label: 'Molecular Formula:', value: formula },
    {
      label: 'Linear Structure Formula:',
      value: isCommercialSupplierData ? null : linearFormula,
    }
  );
}

function renderCommercialLinearFormulaRow(linearFormula, isCommercialSupplierData) {
  if (!isCommercialSupplierData || !linearFormula) {
    return null;
  }

  return renderDescriptionListEntry('Linear Structure Formula:', linearFormula, ROW_VARIANTS.SINGLE_COLUMN);
}

function renderWeightRow(weight, referencesCount, isCIDatabase) {
  if (!weight) {
    return null;
  }

  return renderEntryPair(
    { label: 'Molecular Weight:', value: weight },
    { label: 'No of references:', value: isCIDatabase ? null : referencesCount }
  );
}

function renderSingleColumnRow(label, value) {
  if (!value) {
    return null;
  }

  return renderDescriptionListEntry(label, value, ROW_VARIANTS.SINGLE_COLUMN);
}

function renderIdentificationTable(details, isCIDatabase, isCommercialSupplierData) {
  const { id, type = [], formula, linearFormula, weight, referencesCount, modification, inChiKey, chemicalNames } = details;
  const casRegistryNumbers = (details.casRegistryNumbers || []).join(', ');
  const substanceType = getSubstanceType(type, isCIDatabase);

  return (
    <dl
      className={`${IDENTIFICATION_DATA_CLASS} ${IDENTIFICATION_DATA_CLASS}--description-list ${IDENTIFICATION_DATA_CLASS}--paired-layout results-key-value-wrapper u-els-padding-left-2x`}
      id={IDENTIFICATION_DATA_ID}>
      <SubstanceIdentificationChemicalNamesList chemicalNames={chemicalNames} />
      {renderIdentificationIdRow(id, isCIDatabase, isCommercialSupplierData)}
      {renderRegistryRow(casRegistryNumbers, substanceType)}
      {renderFormulaRow(formula, linearFormula, isCommercialSupplierData)}
      {renderCommercialLinearFormulaRow(linearFormula, isCommercialSupplierData)}
      {renderWeightRow(weight, referencesCount, isCIDatabase)}
      {renderSingleColumnRow('Modification:', modification)}
      {renderSingleColumnRow('InChIKey:', inChiKey)}
    </dl>
  );
}

function renderMixtureItems(items, databaseId, zoomClass, zoom, metadata = {}) {
  return map(items, (structure, index) => (
    <div className={classnames('mixture-structure-wrapper', zoomClass)} key={index}>
      <span className="structure-content-label">
        {structure.name} {structure.amount ? `(${structure.amount})` : ''}
      </span>
      <div className="mixture-structure mixture-structure--react">
        <Structure metadata={metadata} structure={formatStructure(structure, databaseId)} zoom={zoom} />
      </div>
    </div>
  ));
}

function formatStructure(structure, databaseId) {
  // eslint-disable-next-line no-param-reassign
  structure.databaseId = databaseId;
  return new StructureModel(structure);
}

SubstanceIdentificationData.propTypes = {
  metadata: MetadataPropTypesShape,
  details: PropTypes.shape({
    id: PropTypes.string,
    chemicalNames: PropTypes.array,
    casRegistryNumbers: PropTypes.array,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    formula: PropTypes.string,
    linearFormula: PropTypes.string,
    weight: PropTypes.string,
    referencesCount: PropTypes.string,
    modification: PropTypes.string,
    inChiKey: PropTypes.string,
    mixtureItems: PropTypes.array,
  }).isRequired,
  database: PropTypes.object.isRequired,
  zoom: PropTypes.number,
};
